import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  X, MapPin, Calendar, Camera, Edit2, Save, Plus, Trash2, ExternalLink, ChevronLeft, ChevronRight, Flag, ChevronDown, Link2
} from "lucide-react";
import { toast } from "sonner";
import { createEntityLink } from "@/components/EntityEmbed";
import ImageLightbox from "@/components/ImageLightbox";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

// Conservation status info
const CONSERVATION_INFO = {
  LC: { name: "Least Concern", color: "bg-green-100 text-green-700", description: "Population stable, low risk" },
  NT: { name: "Near Threatened", color: "bg-yellow-100 text-yellow-700", description: "Close to qualifying as threatened" },
  VU: { name: "Vulnerable", color: "bg-orange-100 text-orange-700", description: "High risk of extinction in the wild" },
  EN: { name: "Endangered", color: "bg-red-100 text-red-700", description: "Very high risk of extinction" },
  CR: { name: "Critically Endangered", color: "bg-red-200 text-red-900", description: "Extremely high risk of extinction" },
  DD: { name: "Data Deficient", color: "bg-gray-100 text-gray-700", description: "Insufficient data to assess" },
  NE: { name: "Not Evaluated", color: "bg-gray-100 text-gray-600", description: "Has not been assessed" }
};

const SpeciesDetailModal = ({ species, onClose, canEdit, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    ...species,
    taxonomy: species.taxonomy || {
      kingdom: "Animalia",
      phylum: "",
      class_name: "",
      order: "",
      family: "",
      genus: "",
      species: ""
    }
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [revisionData, setRevisionData] = useState({ description: "", field_affected: "" });
  const [submittingRevision, setSubmittingRevision] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showRevisions, setShowRevisions] = useState(false);
  const [sightingDates, setSightingDates] = useState([]);
  const [showSightingDates, setShowSightingDates] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith("http")) return imageUrl;
    return `${BACKEND_URL}${imageUrl}`;
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${species.common_names[0]}"? This action cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.delete(`${API}/admin/marine-database/${species.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Species deleted successfully");
      onClose();
      if (onDelete) onDelete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete species");
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.put(`${API}/marine-database/${species.id}`, {
        common_names: editData.common_names,
        scientific_name: editData.scientific_name,
        simple_category: editData.simple_category,
        description: editData.description,
        habitat: editData.habitat,
        conservation_status: editData.conservation_status,
        seasonality: editData.seasonality,
        taxonomy: editData.taxonomy
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Species updated successfully");
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const formData = new FormData();
      formData.append("file", file);

      await axios.post(`${API}/marine-database/${species.id}/images`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Image uploaded!");
      onUpdate();
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async (imageUrl) => {
    if (!window.confirm("Delete this image?")) return;
    
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.delete(`${API}/marine-database/${species.id}/images?image_url=${encodeURIComponent(imageUrl)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Image deleted");
      setCurrentImageIndex(0);
      onUpdate();
    } catch (error) {
      toast.error("Failed to delete image");
    }
  };

  const MONTHS = ["January", "February", "March", "April", "May", "June", 
                  "July", "August", "September", "October", "November", "December"];

  const toggleMonth = (month) => {
    const current = editData.seasonality || [];
    if (current.includes(month)) {
      setEditData({ ...editData, seasonality: current.filter(m => m !== month) });
    } else {
      setEditData({ ...editData, seasonality: [...current, month] });
    }
  };

  const handleSubmitRevision = async () => {
    if (!revisionData.description.trim()) {
      toast.error("Please describe the issue");
      return;
    }
    setSubmittingRevision(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.post(`${API}/marine-database/revisions`, {
        species_id: species.id,
        species_name: species.common_names[0],
        description: revisionData.description,
        field_affected: revisionData.field_affected || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Revision request submitted for review");
      setShowRevisionForm(false);
      setRevisionData({ description: "", field_affected: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit revision");
    } finally {
      setSubmittingRevision(false);
    }
  };

  const fetchRevisions = async () => {
    try {
      const response = await axios.get(`${API}/marine-database/${species.id}/revisions`);
      setRevisions(response.data);
      setShowRevisions(true);
    } catch (error) {
      console.error("Error fetching revisions:", error);
    }
  };

  const fetchSightingDates = async () => {
    if (showSightingDates) {
      setShowSightingDates(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/marine-database/${species.id}/sighting-dates`);
      setSightingDates(response.data.dates || []);
      setShowSightingDates(true);
    } catch (error) {
      console.error("Error fetching sighting dates:", error);
      setShowSightingDates(true);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border-b">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
              {isEditing ? "Edit Species" : species.common_names[0]}
            </h2>
            {!isEditing && (
              <p className="text-xs sm:text-sm text-gray-500 italic truncate">{species.scientific_name}</p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap sm:justify-end">
            {/* Copy Species Link button */}
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const link = createEntityLink("species", species.id);
                  navigator.clipboard.writeText(link);
                  toast.success("Species link copied! Paste it in chat or forum posts.");
                }}
                className="text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 border-cyan-200"
                data-testid="copy-species-link-btn"
              >
                <Link2 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Copy Link</span>
              </Button>
            )}
            {/* Report Issue button - available for all logged-in users */}
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRevisionForm(!showRevisionForm)}
                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-200"
                data-testid="report-species-issue-btn"
              >
                <Flag className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Revise</span>
              </Button>
            )}
            {canEdit && !isEditing && (
              <>
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  <Edit2 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Edit</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleDelete}
                  disabled={deleting}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                >
                  <Trash2 className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">{deleting ? "Deleting..." : "Delete"}</span>
                </Button>
              </>
            )}
            {isEditing && (
              <>
                <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  <Save className="w-4 h-4 sm:mr-1" /> {saving ? "Saving..." : "Save"}
                </Button>
              </>
            )}
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Revision Form (collapsible) */}
        {showRevisionForm && (
          <div className="border-b bg-amber-50 p-4 space-y-3" data-testid="revision-form">
            <h3 className="font-medium text-amber-800 text-sm">Report an Issue with this Species</h3>
            <div>
              <Label className="text-xs text-amber-700">What needs correcting?</Label>
              <select
                value={revisionData.field_affected}
                onChange={(e) => setRevisionData({ ...revisionData, field_affected: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-amber-200 rounded-md text-sm bg-white"
                data-testid="revision-field-select"
              >
                <option value="">General / Not sure</option>
                <option value="common_names">Common Name</option>
                <option value="scientific_name">Scientific Name</option>
                <option value="taxonomy">Taxonomy Classification</option>
                <option value="description">Description</option>
                <option value="habitat">Habitat</option>
                <option value="conservation_status">Conservation Status</option>
                <option value="images">Images</option>
              </select>
            </div>
            <div>
              <Label className="text-xs text-amber-700">Describe the issue *</Label>
              <Textarea
                value={revisionData.description}
                onChange={(e) => setRevisionData({ ...revisionData, description: e.target.value })}
                placeholder="e.g., Scientific name is misspelled, should be..."
                className="mt-1 border-amber-200 text-sm"
                rows={3}
                data-testid="revision-description"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRevisionForm(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmitRevision}
                disabled={submittingRevision || !revisionData.description.trim()}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="submit-revision-btn"
              >
                {submittingRevision ? "Submitting..." : "Submit Revision"}
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Image Gallery */}
            <div>
              <div className="aspect-square bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl overflow-hidden relative">
                {species.images && species.images.length > 0 ? (
                  <>
                    <img
                      src={getImageUrl(species.images[currentImageIndex])}
                      alt={species.common_names[0]}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setLightboxOpen(true)}
                      title="Click to view full screen"
                    />
                    
                    {/* Navigation arrows */}
                    {species.images.length > 1 && (
                      <>
                        <button
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                          onClick={() => setCurrentImageIndex((currentImageIndex - 1 + species.images.length) % species.images.length)}
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70"
                          onClick={() => setCurrentImageIndex((currentImageIndex + 1) % species.images.length)}
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </>
                    )}

                    {/* Image counter */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1 rounded-full">
                      {currentImageIndex + 1} / {species.images.length}
                    </div>

                    {/* Delete button for editors */}
                    {canEdit && (
                      <button
                        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                        onClick={() => handleDeleteImage(species.images[currentImageIndex])}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Camera className="w-16 h-16 text-gray-300" />
                  </div>
                )}
              </div>

              {/* Thumbnail strip */}
              {species.images && species.images.length > 1 && (
                <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
                  {species.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                        idx === currentImageIndex ? "border-cyan-500" : "border-transparent"
                      }`}
                    >
                      <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Upload button for editors */}
              {canEdit && (
                <div className="mt-3">
                  <label className="flex items-center justify-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-cyan-400 hover:bg-cyan-50 transition-colors">
                    <Camera className="w-4 h-4" />
                    <span className="text-sm">{uploadingImage ? "Uploading..." : "Add Photo"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="space-y-4">
              {isEditing ? (
                // Edit Form
                <>
                  <div>
                    <Label>Common Names (comma separated)</Label>
                    <Input
                      value={editData.common_names?.join(", ") || ""}
                      onChange={(e) => setEditData({ ...editData, common_names: e.target.value.split(",").map(n => n.trim()) })}
                    />
                  </div>
                  <div>
                    <Label>Scientific Name</Label>
                    <Input
                      value={editData.scientific_name || ""}
                      onChange={(e) => setEditData({ ...editData, scientific_name: e.target.value })}
                    />
                  </div>
                  
                  {/* Taxonomy Section */}
                  <div className="border-t pt-3 mt-3">
                    <Label className="text-sm font-semibold text-gray-700 mb-2 block">Taxonomy</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs text-gray-500">Phylum</Label>
                        <Input
                          value={editData.taxonomy?.phylum || ""}
                          onChange={(e) => setEditData({ 
                            ...editData, 
                            taxonomy: { ...editData.taxonomy, phylum: e.target.value }
                          })}
                          placeholder="e.g., Chordata"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Class</Label>
                        <Input
                          value={editData.taxonomy?.class_name || ""}
                          onChange={(e) => setEditData({ 
                            ...editData, 
                            taxonomy: { ...editData.taxonomy, class_name: e.target.value }
                          })}
                          placeholder="e.g., Actinopterygii"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Order</Label>
                        <Input
                          value={editData.taxonomy?.order || ""}
                          onChange={(e) => setEditData({ 
                            ...editData, 
                            taxonomy: { ...editData.taxonomy, order: e.target.value }
                          })}
                          placeholder="e.g., Perciformes"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Family</Label>
                        <Input
                          value={editData.taxonomy?.family || ""}
                          onChange={(e) => setEditData({ 
                            ...editData, 
                            taxonomy: { ...editData.taxonomy, family: e.target.value }
                          })}
                          placeholder="e.g., Pomacentridae"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Genus</Label>
                        <Input
                          value={editData.taxonomy?.genus || ""}
                          onChange={(e) => setEditData({ 
                            ...editData, 
                            taxonomy: { ...editData.taxonomy, genus: e.target.value }
                          })}
                          placeholder="e.g., Amphiprion"
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Species</Label>
                        <Input
                          value={editData.taxonomy?.species || ""}
                          onChange={(e) => setEditData({ 
                            ...editData, 
                            taxonomy: { ...editData.taxonomy, species: e.target.value }
                          })}
                          placeholder="e.g., ocellaris"
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={editData.description || ""}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Habitat</Label>
                    <Input
                      value={editData.habitat || ""}
                      onChange={(e) => setEditData({ ...editData, habitat: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Seasonality (months visible)</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {MONTHS.map((month) => (
                        <button
                          key={month}
                          type="button"
                          onClick={() => toggleMonth(month)}
                          className={`px-2 py-1 text-xs rounded-full border transition-colors ${
                            editData.seasonality?.includes(month)
                              ? "bg-cyan-100 border-cyan-400 text-cyan-700"
                              : "bg-gray-50 border-gray-200 text-gray-600 hover:border-cyan-300"
                          }`}
                        >
                          {month.substring(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                // View Mode
                <>
                  {/* Alternative names */}
                  {species.common_names.length > 1 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Also known as</h4>
                      <div className="flex flex-wrap gap-1">
                        {species.common_names.slice(1).map((name, idx) => (
                          <Badge key={idx} variant="outline">{name}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Conservation Status */}
                  {species.conservation_status && CONSERVATION_INFO[species.conservation_status] && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Conservation Status</h4>
                      <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${CONSERVATION_INFO[species.conservation_status].color}`}>
                        <span className="font-medium">{CONSERVATION_INFO[species.conservation_status].name}</span>
                        <span className="text-sm opacity-75">({species.conservation_status})</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{CONSERVATION_INFO[species.conservation_status].description}</p>
                    </div>
                  )}

                  {/* Description */}
                  {species.description && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Description</h4>
                      <p className="text-gray-700">{species.description}</p>
                    </div>
                  )}

                  {/* Habitat */}
                  {species.habitat && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Habitat</h4>
                      <p className="text-gray-700">{species.habitat}</p>
                    </div>
                  )}

                  {/* Taxonomy */}
                  {species.taxonomy && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Taxonomy</h4>
                      <div className="text-sm text-gray-600 space-y-0.5">
                        {species.taxonomy.phylum && <div><span className="text-gray-400">Phylum:</span> {species.taxonomy.phylum}</div>}
                        {species.taxonomy.class_name && <div><span className="text-gray-400">Class:</span> {species.taxonomy.class_name}</div>}
                        {species.taxonomy.order && <div><span className="text-gray-400">Order:</span> {species.taxonomy.order}</div>}
                        {species.taxonomy.family && <div><span className="text-gray-400">Family:</span> {species.taxonomy.family}</div>}
                      </div>
                    </div>
                  )}

                  {/* Location-based Seasonality */}
                  {species.locations && species.locations.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">Where & When to See</h4>
                      <div className="space-y-3">
                        {/* Group locations by country > area for display */}
                        {(() => {
                          // Build: country -> area -> [dive sites]
                          const countryData = {};
                          species.locations.forEach(loc => {
                            const country = loc.country || "Unknown";
                            const areaKey = loc.location_name || "Other";
                            if (!countryData[country]) {
                              countryData[country] = { country, areas: {} };
                            }
                            if (!countryData[country].areas[areaKey]) {
                              countryData[country].areas[areaKey] = {
                                name: loc.location_name || "",
                                dive_sites: [],
                                total_sightings: 0,
                                months_observed: []
                              };
                            }
                            const area = countryData[country].areas[areaKey];
                            if (loc.dive_site_name && !area.dive_sites.includes(loc.dive_site_name)) {
                              area.dive_sites.push(loc.dive_site_name);
                            }
                            area.total_sightings += loc.times_spotted || 1;
                            (loc.months_observed || []).forEach(month => {
                              if (!area.months_observed.includes(month)) {
                                area.months_observed.push(month);
                              }
                            });
                          });
                          
                          return Object.values(countryData).map((cData, idx) => {
                            const areas = Object.values(cData.areas);
                            const totalSightings = areas.reduce((sum, a) => sum + a.total_sightings, 0);
                            const allSites = areas.flatMap(a => a.dive_sites);
                            const allMonths = [...new Set(areas.flatMap(a => a.months_observed))];
                            
                            return (
                            <div key={idx} className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4 text-cyan-500" />
                                  <span className="font-medium text-gray-800">{cData.country}</span>
                                  {areas.length === 1 && areas[0].name && (
                                    <span className="text-xs text-gray-500">({areas[0].name})</span>
                                  )}
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  {totalSightings} sighting{totalSightings !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                              
                              {/* Show areas if multiple, otherwise just sites */}
                              {areas.length > 1 ? (
                                <div className="space-y-1 mb-2">
                                  {areas.map((area, aIdx) => (
                                    <div key={aIdx} className="text-xs text-gray-500">
                                      {area.name && <span className="font-medium text-gray-600">{area.name}: </span>}
                                      {area.dive_sites.join(", ")}
                                      <span className="text-gray-400 ml-1">({area.total_sightings})</span>
                                    </div>
                                  ))}
                                </div>
                              ) : allSites.length > 0 && (
                                <div className="text-xs text-gray-500 mb-2">
                                  Sites: {[...new Set(allSites)].slice(0, 5).join(", ")}
                                  {allSites.length > 5 && ` +${allSites.length - 5} more`}
                                </div>
                              )}
                              
                              {/* Monthly bar chart */}
                              {allMonths.length > 0 && (
                                <div className="mt-2">
                                  <div className="text-xs text-gray-500 mb-1">Observed months:</div>
                                  <div className="flex gap-0.5">
                                    {MONTHS.map((month) => {
                                      const isObserved = allMonths.includes(month);
                                      return (
                                        <div
                                          key={month}
                                          className={`flex-1 h-6 rounded-sm flex items-end justify-center ${
                                            isObserved ? "bg-cyan-500" : "bg-gray-200"
                                          }`}
                                          title={`${month}${isObserved ? " - observed" : ""}`}
                                        >
                                          <span className={`text-[8px] ${isObserved ? "text-white" : "text-gray-400"}`}>
                                            {month.substring(0, 1)}
                                          </span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );});
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Global Seasonality (for backward compatibility and manual data) */}
                  {species.seasonality && species.seasonality.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">General Best Time to See</h4>
                      <div className="flex flex-wrap gap-1">
                        {species.seasonality.map((month) => (
                          <Badge key={month} variant="secondary" className="text-xs">
                            {month.substring(0, 3)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* First Sighted */}
                  {species.first_sighted_by && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-1">Submitted By</h4>
                      <p className="text-sm text-gray-600">
                        {species.first_sighted_by.user_name}
                        {species.first_sighted_by.date && (
                          <span className="text-gray-400"> • {new Date(species.first_sighted_by.date).toLocaleDateString()}</span>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Stats */}
                  {species.sighting_count > 0 && (
                    <div className="pt-3 border-t">
                      <div className="text-sm text-gray-500">
                        Total sightings: <span className="font-medium text-gray-700">{species.sighting_count}</span>
                      </div>
                      
                      {/* Sighting Dates Dropdown */}
                      <button
                        type="button"
                        onClick={fetchSightingDates}
                        className="mt-2 flex items-center gap-1 text-xs text-cyan-600 hover:text-cyan-700"
                        data-testid="view-sighting-dates-btn"
                      >
                        <Calendar className="w-3 h-3" />
                        {showSightingDates ? "Hide" : "View"} sighting dates
                        <ChevronDown className={`w-3 h-3 transition-transform ${showSightingDates ? "rotate-180" : ""}`} />
                      </button>
                      
                      {showSightingDates && (
                        <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg" data-testid="sighting-dates-list">
                          {sightingDates.length > 0 ? (
                            <table className="w-full text-xs">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-2 py-1 text-left text-gray-500">Date</th>
                                  <th className="px-2 py-1 text-left text-gray-500">Site</th>
                                  <th className="px-2 py-1 text-left text-gray-500">By</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sightingDates.map((s, idx) => (
                                  <tr key={idx} className="border-t">
                                    <td className="px-2 py-1 text-gray-700 whitespace-nowrap">{new Date(s.date + 'T00:00:00').toLocaleDateString()}</td>
                                    <td className="px-2 py-1 text-gray-600 truncate max-w-[100px]" title={s.dive_site}>{s.dive_site || s.country || "-"}</td>
                                    <td className="px-2 py-1 text-gray-500 truncate max-w-[80px]" title={s.reported_by}>{s.reported_by || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="p-3 text-xs text-gray-400 text-center">No recorded sighting dates yet</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Image Lightbox for fullscreen viewing */}
        {species.images && species.images.length > 0 && (
          <ImageLightbox
            images={species.images.map(img => getImageUrl(img))}
            initialIndex={currentImageIndex}
            isOpen={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default SpeciesDetailModal;
